import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface BaseCardProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  badges?: string[];
  className?: string;
  children: React.ReactNode;
  actions?: {
    primary?: {
      text: string;
      link: string;
      icon?: LucideIcon;
    };
    secondary?: {
      text: string;
      onClick: () => void;
      variant?: 'outline' | 'ghost';
    };
  };
  variant?: 'default' | 'gradient' | 'glass';
  colorScheme?: string;
}

export const BaseCard: React.FC<BaseCardProps> = ({
  title,
  description,
  icon: Icon,
  badges = [],
  className,
  children,
  actions,
  variant = 'default',
  colorScheme = 'primary'
}) => {
  const getCardStyles = () => {
    switch (variant) {
      case 'gradient':
        if (colorScheme === 'accent') {
          return 'bg-gradient-to-br from-accent/10 via-background to-accent/5 border-accent/20';
        }
        return 'bg-gradient-to-br from-primary/10 via-background to-primary/5 border-primary/20';
      case 'glass':
        return 'bg-card/80 backdrop-blur-sm border-border/30';
      default:
        return 'bg-card border-border';
    }
  };

  return (
    <Card className={cn(
      "relative overflow-hidden transition-all duration-200 hover:shadow-lg",
      getCardStyles(),
      className
    )}>
      {variant === 'gradient' && (
        <div className={cn(
          "absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-16 translate-x-16 opacity-30",
          colorScheme === 'accent' 
            ? "bg-gradient-to-br from-accent/20 to-accent/30"
            : "bg-gradient-to-br from-primary/20 to-primary/30"
        )}></div>
      )}
      
      <CardHeader className="relative">
        <CardTitle className="flex items-center gap-3">
          {Icon && (
            <div className={cn(
              "p-2 rounded-lg",
              variant === 'gradient' 
                ? colorScheme === 'accent'
                  ? 'bg-gradient-to-r from-accent to-accent/80 text-accent-foreground'
                  : 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            )}>
              <Icon className="h-4 w-4" />
            </div>
          )}
          <div className="flex-1">
            <span className="font-semibold">{title}</span>
            {badges.length > 0 && (
              <div className="flex items-center gap-2 mt-1">
                {badges.map((badge, index) => (
                  <Badge 
                    key={index}
                    variant="secondary" 
                    className={cn(
                      "text-xs",
                      variant === 'gradient' && (
                        colorScheme === 'accent'
                          ? 'bg-accent/10 text-accent border-accent/20'
                          : 'bg-primary/10 text-primary border-primary/20'
                      )
                    )}
                  >
                    {badge}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardTitle>
        {description && (
          <CardDescription>{description}</CardDescription>
        )}
      </CardHeader>
      
      <CardContent className="relative space-y-4">
        {children}
        
        {actions && (
          <div className="flex gap-2 pt-2">
            {actions.primary && (
              <Link to={actions.primary.link} className="flex-1">
                <Button 
                  className={cn(
                    "w-full group",
                    variant === 'gradient' && (
                      colorScheme === 'accent'
                        ? 'bg-gradient-to-r from-accent to-accent/80 hover:from-accent/90 hover:to-accent/70 text-accent-foreground border-0'
                        : 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground border-0'
                    )
                  )}
                >
                  {actions.primary.icon && (
                    <actions.primary.icon className="h-4 w-4 mr-2" />
                  )}
                  {actions.primary.text}
                  <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            )}
            {actions.secondary && (
              <Button 
                variant={actions.secondary.variant || 'outline'}
                onClick={actions.secondary.onClick}
              >
                {actions.secondary.text}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};